import { PERMISSIONS, type Permission } from '@zeroth/shared';
import {
  Bell,
  Building2,
  CircleDollarSign,
  FlaskConical,
  Package,
  Printer,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Tag,
  Truck,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';

/** Store-scoped settings stay limited to the roles that could see them on the single legacy page. */
export const STORE_SETTINGS_PERMISSIONS: Permission[] = [
  PERMISSIONS.STORES_SETTINGS_UPDATE,
  PERMISSIONS.ACCOUNT_USERS_MANAGE,
  PERMISSIONS.ACCOUNT_ROLES_MANAGE,
  PERMISSIONS.STORES_CONNECT,
];

export const PROFILE_SETTINGS_PATH = '/settings/account/profile';

export type SettingsAreaId = 'store' | 'operations' | 'account';

export interface SettingsTab {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Any one of these grants the tab; omitted means every member can open it. */
  permissions?: Permission[];
}

export interface SettingsArea {
  id: SettingsAreaId;
  title: string;
  description: string;
  tabs: SettingsTab[];
}

export const SETTINGS_AREAS: Record<SettingsAreaId, SettingsArea> = {
  store: {
    id: 'store',
    title: 'Store settings',
    description: 'Business details, Amazon connection, and alerts for the selected store',
    tabs: [
      { path: 'general', label: 'General', icon: Building2, permissions: STORE_SETTINGS_PERMISSIONS },
      { path: 'sync', label: 'Amazon & Sync', icon: RefreshCw, permissions: STORE_SETTINGS_PERMISSIONS },
      { path: 'notifications', label: 'Notifications', icon: Bell, permissions: STORE_SETTINGS_PERMISSIONS },
      { path: 'advanced', label: 'Advanced', icon: SlidersHorizontal, permissions: STORE_SETTINGS_PERMISSIONS },
    ],
  },
  operations: {
    id: 'operations',
    title: 'Operations settings',
    description: 'Defaults used by restock planning, FBA prep, labels, and outbound freight',
    tabs: [
      { path: 'restock', label: 'Restock', icon: Package, permissions: STORE_SETTINGS_PERMISSIONS },
      { path: 'prep-costs', label: 'Prep & Costs', icon: Tag, permissions: STORE_SETTINGS_PERMISSIONS },
      { path: 'labels-boxes', label: 'Labels & Boxes', icon: Printer, permissions: STORE_SETTINGS_PERMISSIONS },
      { path: 'outbound-freight', label: 'Outbound Freight', icon: Truck, permissions: [PERMISSIONS.CARRIER_CONFIG_MANAGE] },
    ],
  },
  account: {
    id: 'account',
    title: 'Account settings',
    description: 'Your profile, team access, billing, and account-wide features',
    tabs: [
      { path: 'profile', label: 'Profile', icon: UserRound },
      { path: 'members', label: 'Members', icon: Users, permissions: [PERMISSIONS.ACCOUNT_USERS_MANAGE] },
      { path: 'roles', label: 'Roles', icon: ShieldCheck, permissions: [PERMISSIONS.ACCOUNT_ROLES_MANAGE] },
      { path: 'billing', label: 'Billing & Plans', icon: CircleDollarSign },
      { path: 'labs', label: 'Labs', icon: FlaskConical, permissions: [PERMISSIONS.ACCOUNT_ROLES_MANAGE] },
    ],
  },
};

export function canAccessSettingsTab(tab: SettingsTab, granted: readonly Permission[]): boolean {
  return !tab.permissions?.length || tab.permissions.some((permission) => granted.includes(permission));
}

export function visibleSettingsTabs(area: SettingsArea, granted: readonly Permission[]): SettingsTab[] {
  return area.tabs.filter((tab) => canAccessSettingsTab(tab, granted));
}

export function settingsTabHref(areaId: SettingsAreaId, tabPath: string): string {
  return `/settings/${areaId}/${tabPath}`;
}

/** Section anchors from the single-page layout, kept so old links and bookmarks still land. */
const LEGACY_SECTION_TARGETS: Record<string, string> = {
  profile: PROFILE_SETTINGS_PATH,
  appearance: PROFILE_SETTINGS_PATH,
  billing: settingsTabHref('account', 'billing'),
  'research-scanner': settingsTabHref('account', 'billing'),
  business: settingsTabHref('store', 'general'),
  experimental: settingsTabHref('account', 'labs'),
  team: settingsTabHref('account', 'members'),
  roles: settingsTabHref('account', 'roles'),
  sync: settingsTabHref('store', 'sync'),
  status: settingsTabHref('store', 'sync'),
  inventory: settingsTabHref('operations', 'restock'),
  velocity: settingsTabHref('operations', 'restock'),
  shipping: settingsTabHref('operations', 'prep-costs'),
  labels: settingsTabHref('operations', 'labels-boxes'),
  boxes: settingsTabHref('operations', 'labels-boxes'),
  notifications: settingsTabHref('store', 'notifications'),
  data: settingsTabHref('store', 'advanced'),
  system: settingsTabHref('store', 'advanced'),
};

export function legacySettingsSectionTarget(section: string | null | undefined): string | null {
  if (!section) return null;
  return LEGACY_SECTION_TARGETS[section.replace(/^#/, '')] ?? null;
}

/**
 * Pins only surface for sidebar destinations, so a saved single-page Settings
 * bookmark keeps the area page as its path and carries the tab as `?tab=`.
 */
export function migrateLegacySettingsBookmark(pathname: string, suffix: string): string | null {
  if (pathname === '/settings/outbound-freight') return '/settings/operations?tab=outbound-freight';
  // Organization became Account, which also absorbed the standalone profile page.
  if (pathname === '/settings/org') return `/settings/account${suffix}`;
  if (pathname === '/settings/profile') return '/settings/account?tab=profile';
  if (pathname !== '/settings') return null;

  const legacySection = new URLSearchParams(suffix.split('#', 1)[0]).get('section')
    ?? suffix.split('#')[1];
  const target = legacySettingsSectionTarget(legacySection);
  if (!target) return '/settings/store';
  const [, , areaId, tabPath] = target.split('/');
  return `/settings/${areaId}?tab=${tabPath}`;
}

function isAccessibleSettingsHref(href: string, granted: readonly Permission[]): boolean {
  const [, , areaId, tabPath] = href.split('/');
  const area = SETTINGS_AREAS[areaId as SettingsAreaId];
  const tab = area?.tabs.find((candidate) => candidate.path === tabPath);
  return Boolean(tab && canAccessSettingsTab(tab, granted));
}

/**
 * Where `/settings` lands: a requested legacy section when the member can open it,
 * otherwise the first store or operations tab, otherwise their personal profile.
 */
export function resolveSettingsLanding(
  granted: readonly Permission[],
  legacySection?: string | null,
): string {
  const legacyTarget = legacySettingsSectionTarget(legacySection);
  if (legacyTarget && isAccessibleSettingsHref(legacyTarget, granted)) return legacyTarget;

  for (const areaId of ['store', 'operations'] as const) {
    const tab = visibleSettingsTabs(SETTINGS_AREAS[areaId], granted)[0];
    if (tab) return settingsTabHref(areaId, tab.path);
  }
  return PROFILE_SETTINGS_PATH;
}
